import { useEffect, useCallback, useRef } from 'react';

interface DataSyncEvent {
  type: 'FINANCIAL_DATA_UPDATED' | 'VALUATION_DATA_UPDATED' | 'CATEGORIES_UPDATED';
  hotelId: string;
  timestamp: number;
  source?: string;
}

class DataSyncManager {
  private listeners: Map<string, ((event: DataSyncEvent) => void)[]> = new Map();
  private lastUpdateTimes: Map<string, number> = new Map();

  // Subscribe to data changes for a specific hotel
  subscribe(hotelId: string, callback: (event: DataSyncEvent) => void): () => void {
    if (!this.listeners.has(hotelId)) {
      this.listeners.set(hotelId, []);
    }

    this.listeners.get(hotelId)!.push(callback);

    // Return unsubscribe function
    return () => {
      const callbacks = this.listeners.get(hotelId);
      if (callbacks) {
        const index = callbacks.indexOf(callback);
        if (index > -1) {
          callbacks.splice(index, 1);
        }
      }
    };
  }

  // Notify all subscribers that data has been updated
  notifyDataUpdate(type: DataSyncEvent['type'], hotelId: string, source?: string) {
    const event: DataSyncEvent = {
      type,
      hotelId,
      timestamp: Date.now(),
      source,
    };

    this.lastUpdateTimes.set(`${hotelId}-${type}`, event.timestamp);

    const callbacks = this.listeners.get(hotelId);
    if (callbacks) {
      callbacks.forEach((callback) => {
        try {
          callback(event);
        } catch (error) {
          console.error('Error in data sync callback:', error);
        }
      });
    }

    console.log(`[DataSync] Notified ${callbacks?.length || 0} listeners of ${type} for hotel ${hotelId}`);
  }

  // Check if data needs to be refreshed
  needsRefresh(hotelId: string, type: DataSyncEvent['type'], lastFetchTime: number): boolean {
    const lastUpdateKey = `${hotelId}-${type}`;
    const lastUpdateTime = this.lastUpdateTimes.get(lastUpdateKey) || 0;
    return lastUpdateTime > lastFetchTime;
  }

  // Get the last update time for a data type
  getLastUpdateTime(hotelId: string, type: DataSyncEvent['type']): number {
    return this.lastUpdateTimes.get(`${hotelId}-${type}`) || 0;
  }

  // Force refresh all components for a hotel
  forceRefresh(hotelId: string, source?: string) {
    this.notifyDataUpdate('FINANCIAL_DATA_UPDATED', hotelId, source);
    this.notifyDataUpdate('VALUATION_DATA_UPDATED', hotelId, source);
    this.notifyDataUpdate('CATEGORIES_UPDATED', hotelId, source);
  }
}

// Create singleton instance
export const dataSyncManager = new DataSyncManager();

// React hook for easy integration

export function useDataSync(hotelId: string, onDataUpdated: (event: DataSyncEvent) => void, enabled: boolean = true) {
  const onDataUpdatedRef = useRef(onDataUpdated);
  onDataUpdatedRef.current = onDataUpdated;

  useEffect(() => {
    if (!enabled || !hotelId) return;

    const unsubscribe = dataSyncManager.subscribe(hotelId, (event) => {
      onDataUpdatedRef.current(event);
    });

    return unsubscribe;
  }, [hotelId, enabled]);

  const notifyUpdate = useCallback(
    (type: DataSyncEvent['type'], source?: string) => {
      if (hotelId) {
        dataSyncManager.notifyDataUpdate(type, hotelId, source);
      }
    },
    [hotelId],
  );

  const forceRefresh = useCallback(
    (source?: string) => {
      if (hotelId) {
        dataSyncManager.forceRefresh(hotelId, source);
      }
    },
    [hotelId],
  );

  return { notifyUpdate, forceRefresh };
}

// Utility to create cache-busting URLs
export function createCacheBustingUrl(baseUrl: string, lastUpdateTime?: number): string {
  const separator = baseUrl.includes('?') ? '&' : '?';
  const timestamp = lastUpdateTime || Date.now();
  return `${baseUrl}${separator}_t=${timestamp}`;
}

// Export types for use in components
export type { DataSyncEvent };
